import { useState, useMemo } from "react";
import { num, toCm, ml, money, ceilStep, minSale, REDONDEOS, svPrice, CalcLayout, FormCol, Card, Field, Sel, HowTo, Advanced, ResultCard, CutDiagram } from "./shared";

export default function Cojines({ onSave, s }) {
  const [u, setU] = useState("cm");
  const [tipo, setTipo] = useState("Cuadrado");
  const [ancho, setAncho] = useState("50");
  const [alto, setAlto] = useState("50");
  const [cantidad, setCantidad] = useState("5");
  const [rollo, setRollo] = useState("150");
  const [tela, setTela] = useState("Lisa");
  const [orient, setOrient] = useState("Libre");
  const [rapport, setRapport] = useState("0");
  const [confeccion, setConfeccion] = useState("Dos caras iguales");
  const [cierre, setCierre] = useState("Invisible");
  const [vivo, setVivo] = useState("No");
  const [cordon, setCordon] = useState("No");
  const [relleno, setRelleno] = useState("Fibra siliconada");
  const [margen, setMargen] = useState("10");
  const [desp, setDesp] = useState("0");
  const [ventaMin, setVentaMin] = useState("1");
  const [redondeo, setRedondeo] = useState("0");
  const [precioTela, setPrecioTela] = useState(svPrice(s, "precio_tela_m"));
  const [precioConf, setPrecioConf] = useState(svPrice(s, "precio_confeccion"));

  const r = useMemo(() => {
    const cant = Math.max(0, Math.round(num(cantidad)));
    const anchoCm = toCm(ancho, u), altoCm = toCm(alto, u), rolloCm = num(rollo) || 150;
    const f = 1 + num(margen) / 100;
    const ac = anchoCm * f, altc0 = altoCm * f;
    const piezas = cant * 2;
    const rap = num(rapport);
    const patronAplica = tela === "Con patrón" || tela === "Estampada" || tela === "Rayada";
    const puedeGirar = orient === "Libre" && tela !== "Direccional";
    const calc = (aCorte, altCorte) => {
      let alt = altCorte;
      if (patronAplica && rap > 0) alt = ceilStep(alt, rap);
      const pf = Math.floor(rolloCm / aCorte);
      if (pf < 1) return null;
      return { telaCm: Math.ceil(piezas / pf) * alt, pf, filas: Math.ceil(piezas / pf), altCorte: alt, aCorte };
    };
    const opt1 = calc(ac, altc0);
    const opt2 = puedeGirar ? calc(altc0, ac) : null;
    let best = opt1;
    if (opt2 && (!best || opt2.telaCm < best.telaCm)) best = opt2;
    let telaM = 0, ventaM = 0, aprov = 0, pieces = [];
    if (best) {
      telaM = (best.telaCm * (1 + num(desp) / 100)) / 100;
      ventaM = ceilStep(minSale(telaM, num(ventaMin)), num(redondeo));
      const usada = piezas * best.aCorte * best.altCorte;
      const comprada = rolloCm * (ventaM * 100);
      aprov = comprada > 0 ? (usada / comprada) * 100 : 0;
      pieces = [{ w: best.aCorte, h: best.altCorte, count: piezas }];
    }
    const cierreM = cierre !== "Sin cierre" ? (anchoCm / 100) * cant * 1.05 : 0;
    const perim = ((anchoCm + altoCm) * 2) / 100;
    const vivoM = vivo === "Sí" ? perim * cant * 1.08 : 0;
    const cordonM = cordon === "Sí" ? perim * cant * 1.08 : 0;
    const cMat = ventaM * num(precioTela);
    const cConf = num(precioConf) * cant;
    const total = cMat + cConf;
    const w = [];
    if (!best) w.push(`El ancho de corte (${ac.toFixed(1)} cm) supera el ancho de la tela: elige una tela más ancha.`);
    if (patronAplica && rap > 0) w.push("El patrón (rapport) aumentó el alto de corte para no cortar el diseño.");
    if (!puedeGirar && orient !== "Libre") w.push("El estampado no permite girar las piezas: no se optimiza por rotación.");
    if (cierre !== "Sin cierre" && anchoCm < 20) w.push("El cierre puede ser mayor que el lado del cojín.");
    const rows = [
      { section: "Cojín" },
      { label: "Tipo", value: tipo }, { label: "Cantidad", value: `${cant}` },
      { label: "Medida final", value: `${anchoCm} × ${altoCm} cm` },
      { label: "Medida de corte", value: best ? `${best.aCorte.toFixed(1)} × ${best.altCorte.toFixed(1)} cm` : "—" },
      { label: "Margen de confección", value: `${num(margen)}%` },
      { label: "Confección", value: confeccion },
      { section: "Optimización de corte" },
      { label: "Piezas requeridas", value: `${piezas}` },
      ...(best ? [{ label: "Piezas por fila", value: `${best.pf}` }, { label: "Filas necesarias", value: `${best.filas}` }] : []),
      { label: "Aprovechamiento de tela", value: `${aprov.toFixed(0)}%` },
      { label: "Desperdicio", value: `${(best ? 100 - aprov : 0).toFixed(0)}%` },
      { label: "Metros lineales requeridos", value: ml(telaM) },
      { label: "Metros lineales a vender", value: ml(ventaM), strong: true },
      { section: "Materiales de confección" },
      ...(cierreM ? [{ label: `Cierre (${cierre})`, value: ml(cierreM) }] : []),
      ...(vivoM ? [{ label: "Vivo/ribete", value: ml(vivoM) }] : []),
      ...(cordonM ? [{ label: "Cordón decorativo", value: ml(cordonM) }] : []),
      { label: "Relleno", value: `${relleno} · ${cant} pza(s)` },
      { section: "Costos" },
      { label: "Tela", value: money(cMat) },
      { label: "Confección", value: money(cConf) },
      { label: "Total estimado", value: money(total), strong: true },
    ];
    const inputs = { u, tipo, ancho, alto, cantidad, rollo, tela };
    return { rows, warnings: w, rollCm: rolloCm, pieces, total, inputs };
  }, [u, tipo, ancho, alto, cantidad, rollo, tela, orient, rapport, confeccion, cierre, vivo, cordon, relleno, margen, desp, ventaMin, redondeo, precioTela, precioConf]);

  return (
    <CalcLayout>
      <FormCol>
        <HowTo title="¿Cómo se calcula la tela para cojines?">
          <p>Se agrega el margen de confección (10 % por defecto), se optimiza el corte probando orientación normal y girada, y se calcula la tela con la distribución de menor desperdicio.</p>
        </HowTo>
        <Card>
          <Sel label="Unidad" value={u} onChange={setU} opts={[["mm", "Milímetros"], ["cm", "Centímetros"], ["m", "Metros"]]} testid="coj-unidad" />
          <Sel label="Tipo de cojín" value={tipo} onChange={setTipo} opts={["Cuadrado", "Rectangular", "Redondo", "Cilíndrico", "Banco", "Decorativo", "Para silla", "Para exterior", "Personalizado"]} testid="coj-tipo" />
          <Field label="Ancho final" value={ancho} onChange={setAncho} suffix={u} testid="coj-ancho" />
          <Field label="Alto final" value={alto} onChange={setAlto} suffix={u} testid="coj-alto" />
          <Field label="Cantidad de cojines" value={cantidad} onChange={setCantidad} step="1" testid="coj-cantidad" />
          <Sel label="Ancho de la tela (cm)" value={rollo} onChange={setRollo} opts={["140", "150", "160", "180", "280", "300"]} testid="coj-rollo" />
          <Sel label="Tipo de tela" value={tela} onChange={setTela} opts={["Lisa", "Con patrón", "Direccional", "Terciopelo", "Rayada", "Estampada"]} testid="coj-tela" />
          <Sel label="Orientación del estampado" value={orient} onChange={setOrient} opts={["Libre", "Obligatoria", "No puede girarse"]} testid="coj-orient" />
          {tela !== "Lisa" && <Field label="Rapport (cm)" value={rapport} onChange={setRapport} step="1" suffix="cm" testid="coj-rapport" />}
          <Sel label="Confección" value={confeccion} onChange={setConfeccion} opts={["Dos caras iguales", "Frente decorativo y reverso liso", "Doble vista", "Con cierre", "Sin cierre"]} testid="coj-confeccion" />
          <Sel label="Tipo de cierre" value={cierre} onChange={setCierre} opts={["Invisible", "Nylon", "Metálico", "Velcro", "Botones", "Sin cierre"]} testid="coj-cierre" />
          <Sel label="Vivo / ribete" value={vivo} onChange={setVivo} opts={["No", "Sí"]} testid="coj-vivo" />
          <Sel label="Cordón decorativo" value={cordon} onChange={setCordon} opts={["No", "Sí"]} testid="coj-cordon" />
          <Sel label="Relleno" value={relleno} onChange={setRelleno} opts={["Fibra siliconada", "Hule espuma", "Pluma", "Mixto", "Sin relleno"]} testid="coj-relleno" />
        </Card>
        <Advanced>
          <Field label="Margen de confección (%)" value={margen} onChange={setMargen} step="1" suffix="%" testid="coj-margen" />
          <Sel label="Desperdicio adicional" value={desp} onChange={setDesp} opts={[["0", "Sin desperdicio"], ["3", "3%"], ["5", "5%"], ["8", "8%"], ["10", "10%"]]} testid="coj-desp" />
          <Sel label="Venta mínima" value={ventaMin} onChange={setVentaMin} opts={[["0.50", "0.50 m"], ["1", "1.00 m"]]} testid="coj-ventamin" />
          <Sel label="Redondeo comercial" value={redondeo} onChange={setRedondeo} opts={REDONDEOS} testid="coj-redondeo" />
          <Field label="Precio tela ($/m)" value={precioTela} onChange={setPrecioTela} suffix="$" testid="coj-precio-tela" />
          <Field label="Confección ($/cojín)" value={precioConf} onChange={setPrecioConf} suffix="$" testid="coj-precio-conf" />
        </Advanced>
      </FormCol>
      <ResultCard title="Cotización de Cojines — GOÛT" modality="cojines" rows={r.rows} warnings={r.warnings} total={r.total} inputs={r.inputs} onSave={onSave}
        diagram={<CutDiagram rollCm={r.rollCm} pieces={r.pieces} />} />
    </CalcLayout>
  );
}
